import { isMotionalUser } from 'packages/lib/auth/utils';
import { User } from 'packages/lib/api/annotations-app-api/contract';

export enum Permissions {
  ClassificationEnrichmentView = 'classification-enrichment:view',
  ClassificationEnrichmentLabel = 'classification-enrichment:annotator',
  ClassificationEnrichmentQA = 'classification-enrichment:qa',
  ClassificationEnrichmentAudit = 'classification-enrichment:audit',
  ClassificationEnrichmentAdmin = 'classification-enrichment:admin',
  SceneEnrichmentView = 'scene-enrichment:view',
  SceneEnrichmentLabel = 'scene-enrichment:annotator',
  SceneEnrichmentQA = 'scene-enrichment:qa',
  SceneEnrichmentAudit = 'scene-enrichment:audit',
  SceneEnrichmentAdmin = 'scene-enrichment:admin',
  LidarBoxView = 'lidar-box:view',
  LidarBoxLabel = 'lidar-box:annotator',
  LidarBoxQA = 'lidar-box:qa',
  LidarBoxAudit = 'lidar-box:audit',
  LidarBoxAdmin = 'lidar-box:admin',
  LidarTopDownView = 'lidar-top-down:view',
  LidarTopDownLabel = 'lidar-top-down:annotator',
  LidarTopDownQA = 'lidar-top-down:qa',
  LidarTopDownAudit = 'lidar-top-down:audit',
  LidarTopDownAdmin = 'lidar-top-down:admin',
  LidarSegView = 'lidar-seg:view',
  LidarSegLabel = 'lidar-seg:annotator',
  LidarSegQA = 'lidar-seg:qa',
  LidarSegAudit = 'lidar-seg:audit',
  LidarSegAdmin = 'lidar-seg:admin',
  InstanceSegView = 'instance-seg:view',
  InstanceSegLabel = 'instance-seg:annotator',
  InstanceSegQA = 'instance-seg:qa',
  InstanceSegAudit = 'instance-seg:audit',
  InstanceSegAdmin = 'instance-seg:admin',
  SemSegView = 'sem-seg:view',
  SemSegLabel = 'sem-seg:annotator',
  SemSegQA = 'sem-seg:qa',
  SemSegAudit = 'sem-seg:audit',
  SemSegAdmin = 'sem-seg:admin',
  EvaView = 'eva:view',
  EvaLabel = 'eva:annotator',
  EvaQA = 'eva:qa',
  EvaAudit = 'eva:audit',
  EvaAdmin = 'eva:admin',
  AudioLabelingView = 'audio-labeling:view',
  AudioLabelingLabel = 'audio-labeling:annotator',
  AudioLabelingQA = 'audio-labeling:qa',
  AudioLabelingAudit = 'audio-labeling:audit',
  AudioLabelingAdmin = 'audio-labeling:admin',
  TrafficLightDetectionView = 'traffic-light-detection:view',
  TrafficLightDetectionLabel = 'traffic-light-detection:annotator',
  TrafficLightDetectionQA = 'traffic-light-detection:qa',
  TrafficLightDetectionAudit = 'traffic-light-detection:audit',
  TrafficLightDetectionAdmin = 'traffic-light-detection:admin',
  LidarBoxEnrichmentView = 'lidar-box-enrichment:view',
  LidarBoxEnrichmentLabel = 'lidar-box-enrichment:annotator',
  LidarBoxEnrichmentQA = 'lidar-box-enrichment:qa',
  LidarBoxEnrichmentAudit = 'lidar-box-enrichment:audit',
  LidarBoxEnrichmentAdmin = 'lidar-box-enrichment:admin',
  LidarBoxAmplificationView = 'lidar-box-amplification:view',
  LidarBoxAmplificationLabel = 'lidar-box-amplification:annotator',
  LidarBoxAmplificationQA = 'lidar-box-amplification:qa',
  LidarBoxAmplificationAudit = 'lidar-box-amplification:audit',
  LidarBoxAmplificationAdmin = 'lidar-box-amplification:admin',
  WorkforceManagementView = 'workforce-management:view',
  WorkforceManagementAdmin = 'workforce-management:admin',
}

export enum PermissionRole {
  View = 'view',
  Annotator = 'annotator',
  QA = 'qa',
  Audit = 'audit',
  Admin = 'admin',
}

export const permissionGroups: Record<string, string> = {
  view: 'View Only',
  annotator: 'Annotator',
  qa: 'QA',
  audit: 'Auditor',
  admin: 'Admin',
};

export type PermissionGroups = keyof typeof permissionGroups;

export interface UserWithPermission {
  user_id: string;
  permissions: string;
}

export function userHasPermission(
  user: UserWithPermission,
  permission: string | Permissions
): boolean {
  const permissionsSet = new Set(user.permissions.split(','));
  return permissionsSet.has(permission.toString());
}

export function userHasAnyPermission(
  user: UserWithPermission,
  permissions: (string | Permissions)[]
): boolean {
  const permissionsSet = new Set(user.permissions.split(','));
  return permissions.some((permission) =>
    permissionsSet.has(permission.toString())
  );
}

export function userHasAllPermissions(
  user: UserWithPermission,
  permissions: (string | Permissions)[]
): boolean {
  const permissionsSet = new Set(user.permissions.split(','));
  return permissions.every((permission) =>
    permissionsSet.has(permission.toString())
  );
}

export function addPermissionToUser(
  user: UserWithPermission,
  permission: string | Permissions
): UserWithPermission {
  const permissionsMap: Record<string, string> = {};

  if (user.permissions) {
    for (const p of user.permissions.split(',')) {
      const [tool] = p.split(':');
      if (tool) permissionsMap[tool] = p;
    }
  }

  // Add or overwrite with the new permission.
  const [newTool] = permission.split(':');
  permissionsMap[newTool] = permission.toString();

  return {
    ...user,
    permissions: Object.values(permissionsMap).join(','),
  };
}

export function addPermissionsToUserByRole(
  user: UserWithPermission,
  role: string | PermissionRole
): UserWithPermission {
  const permissionsToAdd = Object.values(Permissions).filter((p) =>
    p.includes(':' + role.toString())
  );
  const newUser = Object.assign({}, user);
  permissionsToAdd.forEach((p) => {
    const updatedUser = addPermissionToUser(newUser, p);
    newUser.permissions = updatedUser.permissions;
  });
  return newUser;
}

export function getUserToolRole(
  user: UserWithPermission,
  tool: string
): PermissionRole {
  const permission = user.permissions
    .split(',')
    .find((p) => p.startsWith(tool + ':'));
  return permission
    ? (permission.split(':')[1] as PermissionRole)
    : PermissionRole.View;
}

export function userHasAnyRole(
  user: UserWithPermission,
  roles: PermissionRole[]
): boolean {
  return user.permissions.split(',').some((p) => {
    const [, permission] = p.split(':');
    return roles.includes(permission as PermissionRole);
  });
}

export function getUserHighestRole(
  user?: UserWithPermission | null
): PermissionRole {
  if (!user) return PermissionRole.View;
  const roleSet = new Set(
    user.permissions.split(',').map((p) => {
      const [, permission] = p.split(':');
      return permission as PermissionRole;
    })
  );
  if (roleSet.has(PermissionRole.Admin)) return PermissionRole.Admin;
  if (roleSet.has(PermissionRole.Audit)) return PermissionRole.Audit;
  if (roleSet.has(PermissionRole.QA)) return PermissionRole.QA;
  if (roleSet.has(PermissionRole.Annotator)) return PermissionRole.Annotator;
  return PermissionRole.View;
}

export function getUserToolsForRole(
  role: PermissionRole,
  user?: UserWithPermission | null
): string[] {
  if (!user) return [];
  return user.permissions
    .split(',')
    .filter((p) => p.endsWith(':' + role))
    .map((p) => p.split(':')[0]);
}

/* The following opening logic determines the default tool filter based on the user's highest role and associated tools.
 * If the user is a Team Lead (Admin role without being a Motional user) and has specific tools associated with that role,
 * we set the default filter to those tools. Otherwise, we default to 'all' to show all users regardless of tool association.
 */
export function isTeamLead(user: User): boolean {
  const highestRole = getUserHighestRole(user);
  const isTeamLead =
    highestRole === PermissionRole.Admin && !isMotionalUser(user);
  return isTeamLead;
}
